/*
 * The Pulse Orb's motion: how the two voices are followed and what moves between
 * frames (scale spring, spin, surface and ring phases, the thinking breath).
 * Drawing lives in kaiVoiceOrb.ts. The dashboard's softphone keeps a copy of both
 * files (gleap-frontend src/components/Softphone/callWidget/), so change them together.
 */

/** 0..1 per side, RMS × 3 like the old merged level. */
export interface VoiceLevels {
  /** The far end of the call: Kai in the customer's widget, Kai or the customer in the dashboard. */
  agent: number;
  /** The person on this device: the customer in the widget, the teammate in the dashboard. */
  caller: number;
}

export const SILENT_LEVELS: VoiceLevels = { agent: 0, caller: 0 };

const TAU = Math.PI * 2;

// Envelopes (ms): a fast one that follows syllables, a slow one for "who has the turn".
const FAST_ATTACK = 22;
const FAST_RELEASE = 160;
const SLOW_ATTACK = 240;
const SLOW_RELEASE = 1000;
// A syllable starts when the fast envelope runs ahead of the slow one.
const ONSET_THRESHOLD = 0.13;
const KICK_GAP_MS = 95;
const RING_GAP_MS = 150;
export const RING_LIFE_MS = 950;
// Scale spring: stiffness / damping (ζ ≈ 0.53, a small quick overshoot per syllable).
const SPRING_K = 200;
const SPRING_C = 15;
// Spin (rad/ms): one turn per minute at rest, much faster while Kai talks.
const BASE_SPIN = TAU / 60000;
const SPEAK_SPIN = TAU / 8000;
const LISTEN_SPIN = TAU / 150000;
// Thinking: the spin winds up and the breath eases in and out.
const THINK_SPIN = TAU / 9000;
const THINK_EASE = 0.06;

/** A ring of light the caller's syllable sends out from the centre. */
export interface Ring {
  start: number;
  strength: number;
}

/** A level follower: frame-rate independent, separate attack and release. */
export interface Envelope {
  value: number;
  update: (target: number, dt: number) => number;
}

export const createEnvelope = (attackMs: number, releaseMs: number): Envelope => {
  const envelope: Envelope = {
    value: 0,
    update: (target, dt) => {
      const tau = target > envelope.value ? attackMs : releaseMs;
      envelope.value += (target - envelope.value) * (1 - Math.exp(-dt / tau));
      return envelope.value;
    },
  };
  return envelope;
};

/** Everything that moves between frames. */
export interface OrbState {
  agentFast: Envelope;
  agentSlow: Envelope;
  callerFast: Envelope;
  callerSlow: Envelope;
  spinVelocity: Envelope;
  scale: number;
  scaleVelocity: number;
  lastKick: number;
  lastRing: number;
  rings: Ring[];
  spin: number;
  waveTime: number;
  rippleTime: number;
  twinkleTime: number;
  thinkAmount: number;
  thinkSpin: number;
  lastTime: number;
}

const createSpinVelocity = () => {
  const spinVelocity = createEnvelope(450, 900);
  spinVelocity.value = BASE_SPIN;
  return spinVelocity;
};

export const createState = (): OrbState => ({
  agentFast: createEnvelope(FAST_ATTACK, FAST_RELEASE),
  agentSlow: createEnvelope(SLOW_ATTACK, SLOW_RELEASE),
  callerFast: createEnvelope(FAST_ATTACK, FAST_RELEASE),
  callerSlow: createEnvelope(SLOW_ATTACK, SLOW_RELEASE),
  spinVelocity: createSpinVelocity(),
  scale: 1,
  scaleVelocity: 0,
  lastKick: -Infinity,
  lastRing: -Infinity,
  rings: [],
  spin: 0.6,
  waveTime: 0,
  rippleTime: 0,
  twinkleTime: 0,
  thinkAmount: 0,
  thinkSpin: 0,
  lastTime: 0,
});

/** Lifts quiet speech so a soft voice still reads; RMS × 3 rarely passes ~0.8. */
const shapeLevel = (level: number) => Math.min(1, Math.pow(Math.min(Math.max(level || 0, 0), 1), 0.7) * 1.15);

/** Follows both voices; under reduced motion they read as silence. */
export const stepLevels = (
  state: OrbState,
  { levels, dt, reducedMotion }: { levels: VoiceLevels; dt: number; reducedMotion: boolean },
) => {
  const agent = reducedMotion ? 0 : shapeLevel(levels.agent);
  const caller = reducedMotion ? 0 : shapeLevel(levels.caller);
  state.agentFast.update(agent, dt);
  state.agentSlow.update(agent, dt);
  state.callerFast.update(caller, dt);
  state.callerSlow.update(caller, dt);
};

export const stepThinking = (
  state: OrbState,
  { thinking, dt, reducedMotion }: { thinking: boolean; dt: number; reducedMotion: boolean },
) => {
  state.thinkAmount += ((thinking && !reducedMotion ? 1 : 0) - state.thinkAmount) * THINK_EASE;
  if (state.thinkAmount < 0.002) state.thinkAmount = 0;
  state.thinkSpin += dt * THINK_SPIN * state.thinkAmount;
};

/** Syllable onsets: Kai's kick the scale spring, the caller's start a ring. */
const stepOnsets = (state: OrbState, time: number) => {
  const agentOnset = state.agentFast.value - state.agentSlow.value;
  if (agentOnset > ONSET_THRESHOLD && time - state.lastKick > KICK_GAP_MS) {
    state.scaleVelocity += 2.6 * agentOnset;
    state.lastKick = time;
  }
  const callerOnset = state.callerFast.value - state.callerSlow.value;
  if (callerOnset > ONSET_THRESHOLD && time - state.lastRing > RING_GAP_MS) {
    state.rings.push({ start: time, strength: Math.min(1, callerOnset * 2.6) });
    state.lastRing = time;
  }
};

/** Kai's voice swells the sphere, the caller draws it in, silence breathes; spin and surface follow. */
export const stepMotion = (state: OrbState, { time, dt }: { time: number; dt: number }) => {
  const agentFast = state.agentFast.value;
  const agentSlow = state.agentSlow.value;
  const callerFast = state.callerFast.value;
  const callerSlow = state.callerSlow.value;
  const presence = Math.max(agentSlow, callerSlow);
  const breath = 0.03 * Math.sin((time / 4000) * TAU) * (1 - presence);
  const target = 1 + 0.13 * agentSlow + 0.26 * agentFast - 0.11 * callerSlow + breath;
  stepOnsets(state, time);
  const seconds = dt / 1000;
  state.scaleVelocity += (SPRING_K * (target - state.scale) - SPRING_C * state.scaleVelocity) * seconds;
  state.scale += state.scaleVelocity * seconds;
  state.spin += state.spinVelocity.update(BASE_SPIN + SPEAK_SPIN * agentSlow - LISTEN_SPIN * callerSlow, dt) * dt;
  state.waveTime += dt * (0.0006 + 0.0055 * agentFast);
  state.rippleTime += dt * (0.004 + 0.01 * callerFast);
  state.twinkleTime += dt * (1 + 3 * (agentFast + callerFast));
};

/** Drops rings that have run their course. */
export const pruneRings = (state: OrbState, time: number) => {
  state.rings = state.rings.filter((ring) => time - ring.start < RING_LIFE_MS);
};
